import type { PillTone } from "@/components/ui/pill";
import type { ProductionOrder } from "../../types/production-orders.types.js";

// Rótulos/tons de status e cancelamento + formatadores de janela (display puro/testável).
export const OP_STATUS_LABEL: Record<ProductionOrder["status"], string> = {
  SCHEDULED: "Agendada",
  RUNNING: "Em execução",
  COMPLETED: "Concluída",
  CANCELLED: "Cancelada",
};

export const OP_STATUS_TONE: Record<ProductionOrder["status"], PillTone> = {
  SCHEDULED: "brand",
  RUNNING: "success",
  COMPLETED: "neutral",
  CANCELLED: "danger",
};

export const OP_CANCEL_LABEL: Record<NonNullable<ProductionOrder["cancelReason"]>, string> = {
  LINE_BUSY: "Linha ocupada",
  MISSED: "Fora da janela",
  MANUAL: "Cancelada manual",
};

// Origem da OP (como foi criada): MANUAL = iniciada na hora (detalhe da linha/dashboard)
// vs SCHEDULED = agendada/auto-start. Na UI só destacamos MANUAL (agendada é o caso default).
export const OP_ORIGIN_LABEL: Record<ProductionOrder["origin"], string> = {
  SCHEDULED: "Agendada",
  MANUAL: "Manual",
};

// Classes Tailwind por status (espelha OP_STATUS do protótipo): fundo + borda + faixa-accent à
// esquerda + cor do texto. Usado nos blocos da timeline e no tile da lista.
export const OP_STATUS_BLOCK: Record<
  ProductionOrder["status"],
  { bg: string; border: string; accent: string; text: string }
> = {
  SCHEDULED: {
    bg: "bg-brand-50",
    border: "border-brand-500/30",
    accent: "border-l-brand-500",
    text: "text-brand-700",
  },
  RUNNING: {
    bg: "bg-rd-success-50",
    border: "border-rd-success-500/30",
    accent: "border-l-rd-success-500",
    text: "text-rd-success-700",
  },
  COMPLETED: {
    bg: "bg-bg-muted-rd",
    border: "border-border-subtle",
    accent: "border-l-text-tertiary",
    text: "text-text-secondary",
  },
  CANCELLED: {
    bg: "bg-rd-danger-50",
    border: "border-rd-danger-500/30",
    accent: "border-l-rd-danger-500",
    text: "text-rd-danger-700",
  },
};

// Iniciais p/ avatar de responsável (1ª + última palavra).
export function initials(name: string | null | undefined): string {
  if (!name?.trim()) return "—";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "—";
}

// "Produto +N" (primeiro produto + contagem dos demais) p/ blocos compactos.
export function productsLabel(o: Pick<ProductionOrder, "products">): string {
  if (o.products.length === 0) return "—";
  const first = o.products[0]?.name ?? "—";
  return o.products.length > 1 ? `${first} +${o.products.length - 1}` : first;
}

export function statusText(o: Pick<ProductionOrder, "status" | "cancelReason">): string {
  const base = OP_STATUS_LABEL[o.status];
  if (o.status === "CANCELLED" && o.cancelReason)
    return `${base} · ${OP_CANCEL_LABEL[o.cancelReason]}`;
  return base;
}

// HH:mm local (fonte única do "horário" da feature — reusado pelo tooltip da timeline).
export function hmTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function dm(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

// "%" com 1 casa (ou "—" p/ null/undefined). Fonte única dos indicadores de OP (D-3).
export function formatPct(v: number | null | undefined): string {
  return v == null ? "—" : `${v.toFixed(1)}%`;
}

export function formatWindow(startIso: string, endIso: string): string {
  return `${dm(startIso)} ${hmTime(startIso)} – ${hmTime(endIso)}`;
}

export function durationLabel(startIso: string, endIso: string): string {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  const min = Math.round(ms / 60000);
  return `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, "0")}m`;
}

// Regra ÚNICA da célula OEE da lista (consumida por oeeCell p/ CSV E pelo render visível na tabela):
// COMPLETED → valor%, RUNNING → "X% feito" (produzido/planejado), senão "—". `kind` permite ao
// render visível pintar a cor sem reimplementar o branch.
export type OeeCellKind = "completed" | "running" | "none";
export function oeeCellValue(o: ProductionOrder): { kind: OeeCellKind; text: string } {
  if (o.status === "COMPLETED" && o.oee != null) {
    return { kind: "completed", text: `${o.oee.toFixed(1)}%` };
  }
  if (o.status === "RUNNING" && o.producedQty != null && o.plannedQty > 0) {
    return { kind: "running", text: `${Math.round((o.producedQty / o.plannedQty) * 100)}% feito` };
  }
  return { kind: "none", text: "—" };
}

// String da célula OEE (CSV).
export function oeeCell(o: ProductionOrder): string {
  return oeeCellValue(o).text;
}

// ── Prazo do cliente e atraso ────────────────────────────────────────────────

/** dd/MM HH:mm do prazo (mesma compactação da janela); sem prazo ⇒ "—". */
export function dueLabel(iso: string | null): string {
  return iso ? `${dm(iso)} ${hmTime(iso)}` : "—";
}

/** Magnitude do atraso em linguagem de chão de fábrica: "+45 min" | "+2h 15m". */
export function lateLabel(min: number): string {
  if (min < 60) return `+${min} min`;
  return `+${Math.floor(min / 60)}h ${String(min % 60).padStart(2, "0")}m`;
}

// Regra ÚNICA da célula de atraso (CSV + render visível + tooltip da timeline). Três estados bem
// distintos — "sem prazo" NUNCA colapsa em "no prazo": não ter compromisso é diferente de cumpri-lo.
export type LatenessKind = "none" | "onTime" | "late";
export function latenessCellValue(o: Pick<ProductionOrder, "latenessMin">): {
  kind: LatenessKind;
  text: string;
} {
  if (o.latenessMin == null) return { kind: "none", text: "—" };
  if (o.latenessMin === 0) return { kind: "onTime", text: "No prazo" };
  return { kind: "late", text: lateLabel(o.latenessMin) };
}

/** String da célula de atraso (CSV). */
export function latenessCell(o: Pick<ProductionOrder, "latenessMin">): string {
  return latenessCellValue(o).text;
}
